/**
 * Core-published EDGE directory — g1's read side.
 *
 * WHY: so adding an edge never touches g1's config. g1 is configured with its
 * CORES directly (that is the ranked-failover model); a core publishes where
 * the EDGES are; market data still flows g1↔edge directly. The core
 * introduces, then gets out of the way.
 *
 * CONTRACT — pinned to hydracore main @ 420c0ab
 * (`hydracore/docs/g1-core-failover-brief.md`, `src/control/directory.ts`):
 *
 *   GET /api/registry/v2/directory
 *   auth: any known plane principal — `Authorization: Bearer` OR `?token=`.
 *     Either credential matching is sufficient (hydracore @ 2b11e6d). An
 *     earlier core build resolved `p.token === (qTok ?? bearer)`, so a stale or
 *     empty query token shadowed a valid header and 401'd; that bug is fixed
 *     and is NOT a rule to design around. g1 sends a Bearer only because the
 *     relay is the caller and a header is the natural fit, not because the core
 *     forbids the pair.
 *   200 → { publisher, incarnation, generation, edges: [{ edgeId, url, live,
 *           sports }] }
 *   405 on any non-GET (so a CORS preflight is refused, not executed)
 *   503 → { error: 'directory_unavailable' }
 *
 * EDGES ONLY. The directory never publishes cores.
 *
 * ── STALENESS IS CAUSAL, NOT COMPARATIVE ────────────────────────────────────
 * The consumer's own request sequence is the authority; nothing in the payload
 * is. Three rules, in order:
 *
 *   1. Tag every fetch with a locally monotonic request id, and apply a
 *      response ONLY if it answers the latest issued request. This is what
 *      drops delayed and reordered responses — including one carrying an
 *      incarnation that has since been retired.
 *   2. Same `(publisher, incarnation)` as cached ⇒ accept only a GREATER
 *      `generation`. That is the replay/stale guard WITHIN one lineage.
 *   3. Different incarnation ⇒ a different lineage: retire the cached one and
 *      adopt this, because it answered the latest request. Never try to decide
 *      which incarnation is "newer".
 *
 * `incarnation` is OPAQUE — compared for EQUALITY ONLY, never for order.
 * Ordering it is unsound and was rejected in review three times: it was a
 * ULID, whose leading bits are `Date.now()`, so a clock rewind sorts a
 * genuinely later lineage lower, and within one millisecond the remainder is
 * pure randomness (485 of 1,000 later mints sorted below their predecessor).
 * `generation` is monotonic only WITHIN an incarnation — comparing it across
 * cores (HC1@3 vs a fresh HC2@1) or across lifetimes of one core (an hc1 that
 * lost its state file republishes at 1 with strictly newer content) is the
 * same defect wearing a different hat. `publisher` is a reusable operator
 * label, never a comparison key on its own.
 *
 * NOTHING here consults a clock to decide adoption. `fetchedAt` in the cache is
 * diagnostic only — a host clock that moved backwards must not change what g1
 * accepts.
 *
 * RULE 6 — BOOTSTRAP AND OPTIMISATION ONLY, NEVER A RUNTIME DEPENDENCY.
 * Boot never waits on the network: the fleet resolves synchronously from
 * cache-else-env and connects, and the fetch lands afterwards. With every core
 * unreachable g1 keeps running on the persisted directory; with no cache at
 * all it falls back to the build-time env seed.
 *
 * `live` and `sports` are ADVISORY. Ownership is arbitrated from each edge's
 * own `activeSports` announce — nothing here may become the ownership signal.
 * In particular we connect to `live: false` edges: a new edge must be
 * discoverable BEFORE it starts announcing, or the first connection can never
 * be made.
 *
 * ADDITIVE ONLY. A directory answer may add edges; it may never delete or
 * re-point a statically configured feed. That is also what makes applying one
 * to a running fleet safe — the SharedWorker latches its upstream URL per
 * worker name, so re-pointing would need a teardown while a NEW edge simply
 * gets its own worker.
 */

import type { Sport } from '@/views/ValueDashboard/types';
import { normalizeSport } from '@/stores/marketStore';
import { safeRead, safeWrite } from '@/lib/infra/storageGuard';
import { apiFetch } from '@/stores/authStore';
import { makeClaimScope, noteDirectoryClaims, type FeedConfig } from './feedConfig';

export interface DirectoryEdge {
  edgeId: string;
  url: string;
  /** Freshly reporting to the publishing core. Advisory — never a gate. */
  live: boolean;
  /** Sports the edge reports serving as primary, when known — OBSERVED.
   *  Advisory, and UNNORMALIZED on the wire (uppercase by repo convention),
   *  so normalize defensively. */
  sports: string[];
  /** Sports core has ASSIGNED this edge as primary — DESIRED
   *  (hydracore `Danielle-feature/directory-assignments` @ 0361a54).
   *
   *  THE CONSUMER RULE (Dan, 2026-08-15): prefer what core says; where core
   *  offers no direction, fall back to local config. Two levels, and never a
   *  hard-coded preference for a named edge — an edge wins because core
   *  assigned it, never because of what it is called.
   *
   *  A TIE-BREAK, NOT AN AUTHORITY. It may decide WHICH of two sources serves
   *  a sport; it may never stop an edge that is genuinely serving. An
   *  assignment able to subtract ownership would let a development core darken
   *  a production edge — the deployed e1 answers to nobody's dev box.
   *
   *  OPTIONAL on the wire: the merged core (420c0ab) does not publish it yet,
   *  and a required field would make g1 reject that core's whole document and
   *  lose the directory entirely. Absent ⇒ core offers no direction ⇒ local
   *  config decides, which is exactly level two of the rule. */
  assigned?: string[];
}

export interface DirectoryDoc {
  /** Publishing core's operator label. NOT an identity to compare across. */
  publisher: string;
  /** Durable lineage token. OPAQUE — equality only, never order. */
  incarnation: string;
  /** Monotonic WITHIN one incarnation. Bumps only when the edge SET changes,
   *  so a cache survives a core restart that changed nothing. */
  generation: number;
  edges: DirectoryEdge[];
}

/** Last-good directory. `g1xl_` is already in storageGuard's known prefixes,
 *  so resetAllLocalStorage clears it (reset ⇒ env seed, as intended).
 *
 *  NOTE the stored shape changed when lineage and edges were split apart. A
 *  cache written by the previous shape fails the guard and reads as absent, so
 *  such a tab boots from its env seed once and repopulates on the next
 *  refresh — a one-time, self-healing degradation that rule 6 already covers. */
export const DIRECTORY_CACHE_KEY = 'g1xl_feed_directory_v1';
/** How long an edge survives in the cache without any adopted directory
 *  listing it — see `CachedDirectory.seenAt`. Long enough to ride out any
 *  lineage fallthrough or core restart; short enough that a retired engine is
 *  gone the next day. */
export const DIRECTORY_EDGE_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * RETIRING A LINEAGE RETIRES THE COUNTER, NOT THE EDGES (contract clarification,
 * `hydracore/docs/g1-core-failover-brief.md` @ da96e3c). Adopting a new
 * `(publisher, incarnation)` changes which `generation` is authoritative for
 * staleness; it never un-learns an edge. The directory is additive in every
 * direction, always — including this cache.
 *
 * That is why the two halves are stored separately. `lineage` is the counter
 * and is REPLACED on adoption; `edges` is the accumulated union and only ever
 * grows. Storing a whole document instead made a lineage change silently
 * destructive: the fleet stayed additive in-session, but the next boot
 * hydrated from the newest publisher's list alone and forgot everything it had
 * learned from the other.
 *
 * This is routine traffic, not an exceptional path: the relay resolves cores in
 * RANK order, so successive fetches are legitimately answered by different
 * cores, and every hc1↔hc2 fallthrough is a lineage change by construction
 * (incarnation is per-core state).
 */
interface CachedDirectory {
  /** Whose counter is authoritative for staleness right now. Replaced wholesale
   *  when a different lineage is adopted. */
  lineage: { publisher: string; incarnation: string; generation: number };
  /** Every edge g1 has learned, from any lineage — additive across lineage
   *  changes, but NOT immortal: see `seenAt`. */
  edges: DirectoryEdge[];
  /** edgeId → when an adopted directory last LISTED it. "Additive" exists so a
   *  lineage change (hc1↔hc2 fallthrough, a core restart with a smaller list)
   *  never un-learns a live edge — a transient, minutes-scale event. It was
   *  never meant to keep an edge NO directory has named for weeks: node1
   *  browsers were still dialling `he4.qeezi.com` (the pre-SuperHydra plane,
   *  dropped from the env 2026-09-09) on every boot on 2026-09-21, and because
   *  it is up and announcing it could WIN a sport — markets and books from the
   *  wrong plane. An edge unlisted for DIRECTORY_EDGE_TTL_MS is forgotten. */
  seenAt?: Record<string, number>;
  /** Diagnostic ONLY. Never consulted for adoption — see the header. */
  fetchedAt: number;
}

function isDirectoryEdge(v: unknown): v is DirectoryEdge {
  if (typeof v !== 'object' || v === null) return false;
  const e = v as Record<string, unknown>;
  return (
    typeof e.edgeId === 'string' && e.edgeId.length > 0 &&
    typeof e.url === 'string' && /^wss?:\/\//.test(e.url) &&
    typeof e.live === 'boolean' &&
    Array.isArray(e.sports) && e.sports.every((s) => typeof s === 'string') &&
    // Optional — see DirectoryEdge.assigned. Present ⇒ must be well-formed.
    (e.assigned === undefined ||
      (Array.isArray(e.assigned) && e.assigned.every((s) => typeof s === 'string')))
  );
}

/** Structural guard. A document that fails this NEVER overwrites a good
 *  cache — a malformed answer must not cost g1 the edges it already knows. */
export function isDirectoryDoc(v: unknown): v is DirectoryDoc {
  if (typeof v !== 'object' || v === null) return false;
  const d = v as Record<string, unknown>;
  return (
    typeof d.publisher === 'string' && d.publisher.length > 0 &&
    typeof d.incarnation === 'string' && d.incarnation.length > 0 &&
    typeof d.generation === 'number' && Number.isFinite(d.generation) &&
    Array.isArray(d.edges) && d.edges.every(isDirectoryEdge)
  );
}

function isCachedDirectory(v: unknown): v is CachedDirectory {
  if (typeof v !== 'object' || v === null) return false;
  const c = v as Record<string, unknown>;
  const lineage = c.lineage as Record<string, unknown> | undefined;
  return (
    typeof lineage === 'object' && lineage !== null &&
    typeof lineage.publisher === 'string' && lineage.publisher.length > 0 &&
    typeof lineage.incarnation === 'string' && lineage.incarnation.length > 0 &&
    typeof lineage.generation === 'number' && Number.isFinite(lineage.generation) &&
    Array.isArray(c.edges) && c.edges.every(isDirectoryEdge) &&
    typeof c.fetchedAt === 'number'
  );
}

export function readCachedDirectory(): CachedDirectory | null {
  return safeRead<CachedDirectory | null>(DIRECTORY_CACHE_KEY, isCachedDirectory, null);
}

/**
 * Adopt `doc`: take its lineage as the new authoritative counter, and UNION its
 * edges into what we already knew. Never subtractive — an edge learned from a
 * retired lineage stays learned.
 *
 * The union is keyed by `edgeId`, the contract's identity field, with the
 * newest publication winning for a repeated id. That keeps an edge that
 * genuinely moved address (same id, new url) at ONE entry rather than
 * accumulating a stale twin, while still never dropping an id we have seen.
 */
export function writeCachedDirectory(doc: DirectoryDoc, nowMs: number = Date.now()): void {
  const prior = readCachedDirectory();
  const listed = new Set(doc.edges.map((e) => e.edgeId));
  const byId = new Map<string, DirectoryEdge>();
  const seenAt: Record<string, number> = {};
  for (const edge of prior?.edges ?? []) {
    if (listed.has(edge.edgeId)) continue; // re-added below from the newer doc
    // Unlisted by THIS directory: kept only while some adopted directory named
    // it recently. An entry with no stamp predates the stamp; its clock starts
    // NOW rather than dropping it on sight — the very first write after this
    // shipped may be a routine lineage flip answered by a core with a shorter
    // list, and that must never shrink the fleet (the additive contract).
    const last = prior?.seenAt?.[edge.edgeId] ?? nowMs;
    if (nowMs - last > DIRECTORY_EDGE_TTL_MS) continue;
    byId.set(edge.edgeId, edge);
    seenAt[edge.edgeId] = last;
  }
  for (const edge of doc.edges) {
    byId.set(edge.edgeId, edge); // newest wins per id
    seenAt[edge.edgeId] = nowMs;
  }
  safeWrite(DIRECTORY_CACHE_KEY, {
    seenAt,
    lineage: {
      publisher: doc.publisher,
      incarnation: doc.incarnation,
      generation: doc.generation,
    },
    edges: [...byId.values()],
    fetchedAt: Date.now(),
  } satisfies CachedDirectory);
}

/**
 * Supersession, per the causal contract. Assumes the caller has ALREADY
 * confirmed request correlation — that check is rule 1 and lives in the fetch
 * path, because it is about which request a response answers, not about what
 * the response contains.
 *
 * Same lineage ⇒ strictly greater generation. Different lineage ⇒ adopt: it
 * answered the latest request, and which incarnation is "newer" is not a
 * question the payload can answer.
 */
export function shouldAdopt(next: DirectoryDoc, cached: DirectoryDoc | null): boolean {
  if (cached == null) return true;
  const sameLineage = next.publisher === cached.publisher && next.incarnation === cached.incarnation;
  if (!sameLineage) return true;
  return next.generation > cached.generation;
}

/**
 * The directory is fetched from g1's OWN RELAY, same-origin, never from a core
 * directly (Dan, 2026-08-15). The relay holds the core bases and the plane
 * principal in server-side env and does the cross-service call itself, so:
 *   - no plane principal ships inside the JS bundle, where devtools would read
 *     it, and the core never has to admit a browser;
 *   - the core needs no public CORS surface (it serves no
 *     `Access-Control-Allow-Origin` and 405s a preflight, so a deployed
 *     browser could not read a direct answer at all);
 *   - it works in every lane — dev, staging, prod — with no core change.
 * Relay side: `relay/src/routes/feeds.ts`. Rank order lives there too, in
 * HYDRA_CORE_REST_URLS.
 */
export const DIRECTORY_ENDPOINT = '/api/feeds/directory';

// Locally monotonic request id — the ONLY authority on which response is
// current. Module scope so it survives across refreshes for the lifetime of
// the tab, which is exactly the window over which a delayed response can land.
let _requestSeq = 0;

/** The id a caller must present when applying a response. */
export function nextDirectoryRequestId(): number {
  return ++_requestSeq;
}

/** Rule 1: a response may be applied ONLY if it answers the latest issued
 *  request. A slow response overtaken by a newer one is dropped here — before
 *  anything looks at its contents — which is what makes a retired incarnation
 *  arriving late a non-event rather than a revert. */
export function isLatestDirectoryRequest(requestId: number): boolean {
  return requestId === _requestSeq;
}

/** Test seam: reset the request sequence between cases. */
export function __resetDirectoryRequestsForTests(): void {
  _requestSeq = 0;
}

/** Fetch from the highest-ranked core that answers, under one request id for
 *  the whole refresh. Returns null when no core is reachable or none answers
 *  with a valid document — the caller then keeps running on cache or env,
 *  which is the whole point of rule 6. */
export async function fetchDirectory(
  requestId: number,
  fetcher: (path: string) => Promise<Response> = apiFetch,
): Promise<{ doc: DirectoryDoc; requestId: number } | null> {
  try {
    const res = await fetcher(DIRECTORY_ENDPOINT);
    // 503 = no core configured on the relay, or none answered. Indistinguishable
    // from "core down" by design, and handled the same way: keep the cache.
    if (!res.ok) return null;
    const body: unknown = await res.json();
    if (!isDirectoryDoc(body)) {
      console.warn('[feedDirectory] relay returned a malformed directory — ignored');
      return null;
    }
    return { doc: body, requestId };
  } catch {
    // Relay unreachable / non-JSON. Expected offline; the cache carries the
    // fleet, which is what rule 6 requires.
    return null;
  }
}

/** Normalize a wire sports list to g1's vocabulary, dropping unknowns. */
function normalizeList(raw: string[] | undefined): Sport[] {
  const out: Sport[] = [];
  for (const code of raw ?? []) {
    const s = normalizeSport(code);
    if (s && !out.includes(s)) out.push(s);
  }
  return out;
}

/** OBSERVED — what the edge reports serving. */
function normalizedSports(edge: DirectoryEdge): Sport[] {
  return normalizeList(edge.sports);
}

/** DESIRED — what core assigned this edge as primary. */
function normalizedAssigned(edge: DirectoryEdge): Sport[] {
  return normalizeList(edge.assigned);
}

/** One core assignment, resolved to the feed that should be PREFERRED for
 *  those sports. Consumed by marketStore as a tie-break only. */
export interface DirectoryAssignment {
  feedId: string;
  sports: Sport[];
}

/**
 * Merge directory edges into a resolved fleet. ADDITIVE ONLY: an edge whose
 * consumer URL is already configured is skipped entirely, so a static feed is
 * never duplicated, re-pointed, or removed.
 *
 * Returns the NEW feeds only (the caller appends them and starts clients).
 * Each carries a mutable claim set, so a later refresh that newly reports an
 * edge's sports activates the claim on the RUNNING fleet without a rebuild.
 */
export function directoryFeedsFor(doc: DirectoryDoc, existing: FeedConfig[]): FeedConfig[] {
  return resolveDirectoryFeeds(doc, existing).added;
}

/** Feed id for a discovered edge — its LOGICAL identity, stable across a move. */
function feedIdFor(edgeId: string): string {
  return `dir:edge:${edgeId}`;
}

/**
 * Reconcile a directory against the running feed set by LOGICAL identity
 * (`edgeId`), not by URL (G1-DIR-03).
 *
 * The two layers had drifted apart: the durable cache replaced a repeated
 * edgeId, while runtime reconciliation deduplicated by URL. An edge that MOVED
 * from endpoint A to B therefore looked like a brand-new feed, so B was
 * appended while A stayed live — two entries sharing one feed id AND one
 * SharedWorker name. That is worse than a duplicate: the worker latches the
 * first URL it was given, so A could stay authoritative and B never connect at
 * all, while the store saw one logical feed with two clients behind it.
 *
 * `moved` therefore carries a REPLACEMENT for an existing discovered feed. The
 * caller swaps it in place — old client stopped, new one started — so the fleet
 * converges on one coherent identity pointing at the newest endpoint. This is
 * not "removing an edge g1 has learned": the edge is retained, at its current
 * address. Retiring the stale TRANSPORT is what keeps that address honest.
 */
export function resolveDirectoryFeeds(
  doc: DirectoryDoc,
  existing: FeedConfig[],
  /** `readOnly`: compute the merge WITHOUT touching fleet state — no directory
   *  claims noted, no capability absorbed by `existing`. For callers that only
   *  want to know where the edges are (the live game lane picking a URL); the
   *  fleet itself resolves without it. */
  opts: { readOnly?: boolean } = {},
): { added: FeedConfig[]; moved: FeedConfig[]; retired: string[]; assignments: DirectoryAssignment[] } {
  const known = new Set(existing.map((f) => stripToken(f.url)));
  const byFeedId = new Map(existing.map((f) => [f.id, f]));
  const added: FeedConfig[] = [];
  const moved: FeedConfig[] = [];
  const retired: string[] = [];
  const assignments: DirectoryAssignment[] = [];
  /** Core's direction for this edge, routed to whichever feed holds its
   *  socket. Registering it is what makes ownership FOLLOW core; granting the
   *  capability is what makes the edge's announce acceptable in the first
   *  place, since a statically configured feed's scope is otherwise frozen at
   *  what env declared. Both are additive. */
  const direct = (feedId: string, holder: FeedConfig | undefined, assigned: Sport[]) => {
    if (assigned.length === 0) return; // core offers no direction — level two
    if (!opts.readOnly) holder?.absorbDirectoryCapability?.(assigned);
    assignments.push({ feedId, sports: assigned });
  };
  for (const edge of doc.edges) {
    const bare = stripToken(edge.url);
    const feedId = feedIdFor(edge.edgeId);
    const incumbent = byFeedId.get(feedId);
    const incumbentIsDiscovered = incumbent != null && incumbent.id.startsWith('dir:edge:');
    const incumbentUrl = incumbent == null ? null : stripToken(incumbent.url);

    const assigned = normalizedAssigned(edge);

    if (incumbentUrl === bare) {
      // Unchanged transport — but core's direction may have moved.
      direct(feedId, incumbent, assigned);
      continue;
    }

    if (known.has(bare)) {
      // The destination is ALREADY dialled by someone else: a static feed, or a
      // sibling discovered edge. One socket is right — dialling it twice is
      // not. But the moved edge's CAPABILITY must survive the collapse, or we
      // keep the transport and silently drop what the directory said it serves
      // (G1-DIR-06). A static e2@B admitting only MLB would otherwise filter
      // this edge's NFL announce and reject its NFL frames, and for an
      // edge-only sport the firehose is no substitute: OG may advertise it
      // while carrying no game data for it.
      //
      // So the holder of that socket ABSORBS the directory capability — the two
      // claims union, and the holder's scope opens so later announces widen it
      // as a discovered feed's would. Additive only: the operator's own claim
      // is never narrowed.
      const holder = existing.find((f) => stripToken(f.url) === bare);
      // The holder takes on BOTH what this edge is observed serving and what
      // core assigned it. This is also the ordinary path for a STATICALLY
      // configured edge that core has directed: env froze its scope, so
      // without the grant its announce for an assigned sport is filtered and
      // the assignment can never take effect.
      if (!opts.readOnly) holder?.absorbDirectoryCapability?.(normalizedSports(edge));
      if (holder != null) direct(holder.id, holder, assigned);
      // The stale transport still has to go, or it runs on until reload.
      // Retirement is reported separately from addition because they are
      // different operations here.
      if (incumbentIsDiscovered) retired.push(feedId);
      continue;
    }
    if (incumbent != null && !incumbentIsDiscovered) continue; // static owns this id
    known.add(bare);
    // Scope starts from the directory's ADVISORY list and widens from the
    // edge's own announce (G1-DIR-01). It must be able to widen: an edge that
    // has never reported is published `live:false, sports:[]` so it can be
    // dialled before it announces, and a snapshot of that empty list would
    // keep it inert forever.
    // A discovered edge is seeded with BOTH lists: what it observes serving
    // and what core assigned it. Union, never intersection — the two diverge
    // exactly when an edge cannot honour an assignment, and dropping either
    // half would lose real capability.
    const declared = new Set<Sport>([...normalizedSports(edge), ...assigned]);
    if (!opts.readOnly) noteDirectoryClaims(declared);
    const scope = makeClaimScope(declared, { dynamic: true });
    const feed: FeedConfig = {
      id: feedId,
      // Edges are not bearer-gated; the core plane principal never rides an
      // edge URL. (Cores are configured directly and are never published here.)
      url: edge.url,
      // Worker name carries the ENDPOINT, not just the identity: a SharedWorker
      // latches the first URL given to a name and offers no way to re-point it,
      // so reusing the name after a move would keep the stale endpoint live —
      // possibly in another tab that still holds the old worker.
      workerName: `hydra-dir-edge-${edge.edgeId}-${shortHash(bare)}`,
      role: 'edge',
      label: edge.edgeId,
      ...scope,
      // Discovered edges never join the OP ladder: the OP authority is an
      // operator decision expressed in g1's own env, and silently moving
      // ingestion to an engine a core happened to mention would be exactly
      // the runtime dependency rule 6 forbids.
      subscribeOpOdds: false,
    };
    if (incumbentIsDiscovered) moved.push(feed);
    else added.push(feed);
    direct(feedId, feed, assigned);
  }
  return { added, moved, retired, assignments };
}

/** Stable short hash of a URL, for per-endpoint SharedWorker names. djb2 —
 *  no crypto needed, this only has to be deterministic and collision-shy. */
function shortHash(value: string): string {
  let h = 5381;
  for (let i = 0; i < value.length; i++) h = ((h << 5) + h + value.charCodeAt(i)) >>> 0;
  return h.toString(16).slice(0, 6);
}

/** Directory URLs are compared token-free — the same engine configured with a
 *  principal and published without one is still the same engine. */
function stripToken(url: string): string {
  try {
    const u = new URL(url);
    u.searchParams.delete('token');
    u.search = u.searchParams.toString();
    return u.toString();
  } catch {
    return url;
  }
}

/**
 * Resolution order per the brief: live directory → persisted cache →
 * build-time seed. This supplies the middle rung SYNCHRONOUSLY at module load,
 * so the fleet dials learned edges with zero boot latency and no await.
 */
export function cachedDirectoryDoc(): DirectoryDoc | null {
  const cached = readCachedDirectory();
  if (cached == null) return null;
  // The authoritative counter, carrying the ACCUMULATED edge set — so boot
  // hydration dials every edge ever learned, and `shouldAdopt` still compares
  // against the lineage that is current.
  return { ...cached.lineage, edges: cached.edges };
}
